import { Unit } from "@/helpers/Unit";
import { groupBy } from "@/helpers/utils";
import { DroppsCatalog, catalogSelect } from "@/store";
import {
  Address,
  Discount,
  RechargeItemRaw,
  RechargeAddress,
  RechargeDiscount,
  BoxItem,
  RechargeItemFormatted,
} from "./types";
import { Money } from "@/helpers/Money";

type AddressWithDiscount = Address & {
  discount?: Discount;
};

function normalize(s?: string | null): string | undefined {
  const trimmed = s?.toLocaleLowerCase()?.trim();
  if (trimmed === "none") {
    return undefined;
  } else {
    return trimmed || undefined;
  }
}

function addressContatenation(x: RechargeAddress): string {
  return [
    normalize(x.first_name),
    normalize(x.last_name),
    normalize(x.company),
    normalize(x.address1),
    normalize(x.address2),
    normalize(x.city),
    normalize(x.province),
    normalize(x.zip),
  ].join("|");
}

function canonicalAddresses(addresses: RechargeAddress[]) {
  const physicalAddresses = groupBy(addresses, addressContatenation);
  let canonicalAddress: Record<number, number> = {};
  for (const [_, addresses] of physicalAddresses.entries()) {
    const addressesWithDiscounts = addresses.filter(
      (x) => x.discount_id != null
    );

    if (addressesWithDiscounts.length > 1) {
      // Can't consolidate addresses when there is more than one discount code
      continue;
    }

    const firstAddress =
      addressesWithDiscounts[0] ??
      addresses.reduce((acc, x) => (x.id < acc.id ? x : acc), addresses[0]);

    for (const address of addresses) {
      canonicalAddress[address.id] = firstAddress.id;
    }
  }
  return canonicalAddress;
}

function mapDiscount(x: RechargeDiscount): Discount {
  switch (x.discount_type) {
    case "fixed_amount":
      return {
        kind: "fixed",
        name: x.code,
        value: x.value,
      };
    case "percentage":
      return {
        kind: "percent",
        name: x.code,
        value: x.value,
      };
    case "shipping":
      return {
        kind: "freeShipping",
        name: x.code,
      };
  }
}

export function sanitizeData(
  rawActiveRechargeItems: RechargeItemRaw[],
  rawAddressses: RechargeAddress[],
  catalog: DroppsCatalog,
  isWholesaleCustomer: boolean
) {
  const canonicalAddress = canonicalAddresses(rawAddressses);
  const rechargeFormattedItems: RechargeItemFormatted[] =
    rawActiveRechargeItems.map((x) => ({
      ...x,
      price: Money.from(x.price, "$"),
      address_id: canonicalAddress[x.address_id] ?? x.address_id,
      nextChargeDate: x.next_charge_scheduled_at
        ? new Date(x.next_charge_scheduled_at)
        : undefined,
      cadence: x.order_interval_frequency
        ? {
            frequency: +x.order_interval_frequency,
            unit: new Unit(x.order_interval_unit ?? "month"),
          }
        : null,
    }));

  const addresses: AddressWithDiscount[] = rawAddressses.map((x) => {
    const rechargeDiscount =
      "code" in (x.discount ?? {})
        ? (x.discount as RechargeDiscount)
        : undefined;

    const discount = rechargeDiscount
      ? mapDiscount(rechargeDiscount)
      : undefined;
    return {
      id: x.id,
      firstName: x.first_name,
      lastName: x.last_name,
      company: x.company,
      address1: x.address1,
      address2: x.address2,
      city: x.city,
      province: x.province,
      zip: x.zip,
      country: x.country,
      phone: x.phone,
      discount,
    };
  });

  const addressBook = new Map(addresses.map((x) => [x.id, x]));
  const productLookup = new Map(catalog.all.map((x) => [x.id, x]));
  const variants = new Map(
    catalog.all.flatMap((p) =>
      p.variants.flatMap((v) =>
        v.subscription?.kind === "variant"
          ? [
              [v.id, v],
              [v.subscription.variantId, v],
            ]
          : [[v.id, v]]
      )
    )
  );

  const allItems = rechargeFormattedItems.flatMap((rechargeItem) => {
    const product = productLookup.get(rechargeItem.shopify_product_id);
    const address = addressBook.get(rechargeItem.address_id);
    const variant = variants.get(rechargeItem.shopify_variant_id);
    return address && product && variant
      ? [{ rechargeItem, address, product, variant }]
      : [];
  });

  const boxItems = allItems.filter(
    (x): x is BoxItem =>
      x.rechargeItem.nextChargeDate !== undefined &&
      x.rechargeItem.status != "CANCELLED"
  );

  const products = catalogSelect.orderable(catalog, {
    bulk: isWholesaleCustomer ? "include" : "exclude",
    pos: "portal",
  });
  return { allItems, addresses, products, boxItems: boxItems };
}
